
import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { SelectCulturalDefinition } from '../../interfaces/select-cultural-definition';
import { SelectOptions } from '../../interfaces/select-options';
import { Connect } from '../../../../classes/connect';
import { ApiResponse } from '../../../../interfaces/api-response';
import { ConnectServerService } from '../../../../services/connect-server.service';
import { CheckFormValidityService } from '../../../../services/check-form-validity.service';
import { PopupDialogService } from '../../../../services/popup-dialog.service';
import { SelectSourcesDefinition } from '../../interfaces/select-sources-definition';


@Component({
    selector: 'app-sources',
    imports: [
    MatExpansionModule,
    ReactiveFormsModule
],
    templateUrl: './sources.component.html',
    styleUrl: './sources.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourcesComponent {

  readonly panelOpenState = signal(false);

  @Input() idarticle: number = 0;
  @Input() listSourcesData: SelectSourcesDefinition | null = null;

  saved: boolean = false;
  submitted = false;

  sourcesForm: FormGroup;

  constructor(private fb: FormBuilder, private connectServerService: ConnectServerService,
    private popupDialogService: PopupDialogService, private checkFormValidityService: CheckFormValidityService) {
    this.sourcesForm = this.fb.group({
      picturesSources: this.fb.array([this.createPicturesSources()]),
      documentSources: this.fb.array([this.createDocumentSources()]),
      bibliography: this.fb.array([this.createBibliography()]),
      completeCitations: this.fb.array([this.createCompleteCitations()]),
    });
  }

  ngOnInit(): void {
    //TEMPORANEO
    this.getSourcesInfo();
  }

  // Funzione che crea un singolo gruppo di campi
  createPicturesSources(): FormGroup {
    return this.fb.group({
      id: [0],
      owner: [null, Validators.maxLength(250)],
      code: [null, [Validators.required, Validators.maxLength(250)]],
    });
  }

  createDocumentSources(): FormGroup {
    return this.fb.group({
      id: [0],
      type: [null],
      author: [null, Validators.maxLength(50)],
      denomination: [null, Validators.maxLength(250)],
      date: [null, Validators.maxLength(25)],
      paper: [null, Validators.maxLength(250)],
      archiveName: [null, Validators.maxLength(250)],
      position: [null, Validators.maxLength(50)],
    });
  }

  createBibliography(): FormGroup {
    return this.fb.group({
      id: [0],
      genre: [null],
      author: [null, Validators.maxLength(250)],
      year: [null, Validators.maxLength(10)],
      acronym: [null, Validators.maxLength(8)],
      vPpNn: [null, Validators.maxLength(50)],
      vTavvFigg: [null, Validators.maxLength(50)]
    });
  }

  createCompleteCitations(): FormGroup {
    return this.fb.group({
      id: [0],
      completeCitation: [null, Validators.maxLength(500)]
    })
  }

  // Restituisce il FormArray per una gestione più semplice
  get picturesSources(): FormArray {
    return this.sourcesForm.get('picturesSources') as FormArray;
  }

  get documentSources(): FormArray {
    return this.sourcesForm.get('documentSources') as FormArray;
  }

  get bibliography(): FormArray {
    return this.sourcesForm.get('bibliography') as FormArray;
  }

  get completeCitations(): FormArray {
    return this.sourcesForm.get('completeCitations') as FormArray;
  }

  // Aggiunge un nuovo gruppo di campi al FormArray
  addPicturesSources() {
    this.picturesSources.push(this.createPicturesSources());
  }

  addDocumentSources() {
    this.documentSources.push(this.createDocumentSources());
  }

  addBibliography() {
    this.bibliography.push(this.createBibliography());
  }

  addCompleteCitations() {
    this.completeCitations.push(this.createCompleteCitations());
  }

  // Rimuove un gruppo di campi dal FormArray, lasciando sempre almeno uno
  removePicturesSources(index: number) {
    if (this.picturesSources.length > 1) {
      this.picturesSources.removeAt(index);
    }
  }

  removeDocumentSources(index: number) {
    if (this.documentSources.length > 1) {
      this.documentSources.removeAt(index);
    }
  }

  removeBibliography(index: number) {
    if (this.bibliography.length > 1) {
      this.bibliography.removeAt(index);
    }
  }

  removeCompleteCitations(index: number) {
    if (this.completeCitations.length > 1) {
      this.completeCitations.removeAt(index);
    }
  }

  private getSourcesInfo() {
    this.connectServerService.getRequest('sourcesInfo', { idarticle: this.idarticle })
      .subscribe(
        (val: ApiResponse<{ infoSources: { picturesSources: any[], documentSources: any[], bibliography: any[], completeCitations: any[] } }>) => {
          // console.log('val:', val.data.infoSources);

          // Pulisci tutti gli array esistenti
          this.picturesSources.clear();
          this.documentSources.clear();
          this.bibliography.clear();
          this.completeCitations.clear();

          // Popola l'array picturesSources
          if (val.data.infoSources.picturesSources && val.data.infoSources.picturesSources.length > 0) {
            val.data.infoSources.picturesSources.forEach(picture => {
              const pictureGroup = this.createPicturesSources();
              pictureGroup.patchValue(picture);
              this.picturesSources.push(pictureGroup);
            });
          } else {
            // Se non ci sono dati, mantieni almeno un elemento vuoto
            this.picturesSources.push(this.createPicturesSources());
          }

          // Popola l'array documentSources
          if (val.data.infoSources.documentSources && val.data.infoSources.documentSources.length > 0) {
            val.data.infoSources.documentSources.forEach(document => {
              const documentGroup = this.createDocumentSources();
              documentGroup.patchValue(document);
              this.documentSources.push(documentGroup);
            });
          } else {
            // Se non ci sono dati, mantieni almeno un elemento vuoto
            this.documentSources.push(this.createDocumentSources());
          }

          // Popola l'array bibliography
          if (val.data.infoSources.bibliography && val.data.infoSources.bibliography.length > 0) {
            val.data.infoSources.bibliography.forEach(biblio => {
              const biblioGroup = this.createBibliography();
              biblioGroup.patchValue(biblio);
              this.bibliography.push(biblioGroup);
            });
          } else {
            // Se non ci sono dati, mantieni almeno un elemento vuoto
            this.bibliography.push(this.createBibliography());
          }

          // Popola l'array completeCitations
          if (val.data.infoSources.completeCitations && val.data.infoSources.completeCitations.length > 0) {
            val.data.infoSources.completeCitations.forEach(citation => {
              const citationGroup = this.createCompleteCitations();
              citationGroup.patchValue(citation);
              this.completeCitations.push(citationGroup);
            });
          } else {
            // Se non ci sono dati, mantieni almeno un elemento vuoto
            this.completeCitations.push(this.createCompleteCitations());
          }

          this.saved = this.checkFormValidityService.areAllRequiredFieldsFilled(this.sourcesForm);
          if (this.saved) {
            this.panelOpenState.set(false);
          }
          else {
            this.panelOpenState.set(true);
          }
        }
      )
  }

  private setSourcesInfo() {
    const formValues = this.sourcesForm.getRawValue();
    // console.log(formValues);
    this.connectServerService.postRequest('updateSourcesInfo',
      { idarticle: this.idarticle, infoSources: formValues })
      .subscribe(
        (val: ApiResponse<null>) => {
          this.popupDialogService.popupDialog(val);
          this.getSourcesInfo();
        }
      )
  }

  save() {
    this.submitted = true;
    if (this.sourcesForm.valid) {
      this.setSourcesInfo();
    }
  }

}
